import { Checkbox } from "components/designV2/checkbox/checkbox";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TimeReportsV2Group } from "services/api/company/timeReports.service";
import { useWorkerAvatar } from "web-apps/company/components/common/hooks/useWorkerAvatar";
import { getTagIconColor } from "./personRow.presentation";
import {
  PersonSummaryDetails,
  PersonSummaryLabels,
} from "./personSummaryDetails.component";
import { PersonTimeReportRow } from "./personTimeReportRow.component";
import { isZeroLikeValue } from "./personRow.mapper";
import { DetailsList, RowCard, SelectAllRow } from "./personSummaryRow.styled";
import type { PersonDetailSelection } from "../../models/personView.types";
import { usePersonRowViewModel } from "../../hooks/usePersonRowViewModel";

interface PersonRowProps {
  row: TimeReportsV2Group;
  isExpandedByDefault: boolean;
  deviationToleranceMinutes: number;
  includeFlexTimeDeviations: boolean;
  selectedDetailIds: number[];
  onDownloadClick?: (row: TimeReportsV2Group) => void;
  onSelectionChange?: (
    selection: PersonDetailSelection[],
    checked: boolean,
  ) => void;
  onDetailClick?: (selection: PersonDetailSelection) => void;
}

export const PersonRow = (props: PersonRowProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(props.isExpandedByDefault);
  const { onDetailClick, onSelectionChange, onDownloadClick, row } = props;

  const {
    teamLabel,
    summary,
    detailEntries,
    rowSelections,
    selectionByDetailId,
    selectedDetailIdSet,
    isAllSelected,
    isPartiallySelected,
  } = usePersonRowViewModel({
    row,
    deviationToleranceMinutes: props.deviationToleranceMinutes,
    includeFlexTimeDeviations: props.includeFlexTimeDeviations,
    selectedDetailIds: props.selectedDetailIds,
    locale: i18n.language,
    translate: t,
  });

  const avatarUrl = useWorkerAvatar({
    workerExternalId: row.externalId,
    isWorker: true,
    initialUrl: null,
  });

  const labels: PersonSummaryLabels = {
    shifts: t("TimeReports.Shifts"),
    plannedTime: t("TimeReports.PlannedTime"),
    approvedTime: t("TimeReports.ApprovedTime"),
    toApprove: t("TimeReports.ToApprove"),
    unreported: t("TimeReports.Unreported"),
    absence: t("TimeReports.Absence"),
    deviation: t("TimeReports.Deviation"),
    download: t("TimeReports.Download"),
    collapse: t("TimeReports.CollapseRow"),
    expand: t("TimeReports.ExpandRow"),
  };

  useEffect(() => {
    setIsExpanded(props.isExpandedByDefault);
  }, [props.isExpandedByDefault]);

  const handleDetailClick = useCallback(
    (detailId: number): void => {
      if (!onDetailClick) return;
      const selection = selectionByDetailId.get(detailId);
      if (!selection) return;
      onDetailClick(selection);
    },
    [onDetailClick, selectionByDetailId],
  );

  const handleSelectAllChange = useCallback(
    (checked: boolean): void => {
      onSelectionChange?.(rowSelections, checked);
    },
    [onSelectionChange, rowSelections],
  );

  const handleDownloadClick = useCallback((): void => {
    onDownloadClick?.(row);
  }, [onDownloadClick, row]);

  const handleDetailSelectionChange = useCallback(
    (detailId: number, checked: boolean): void => {
      const selection = selectionByDetailId.get(detailId);
      if (!selection) return;
      onSelectionChange?.([selection], checked);
    },
    [onSelectionChange, selectionByDetailId],
  );

  const rowInteractionHandlersById = useMemo<
    Map<
      number,
      {
        onOpenDetails: () => void;
        onToggleSelection: (checked: boolean) => void;
      }
    >
  >(() => {
    return new Map(
      detailEntries.map((entry) => {
        const detailId = entry.selection.detail.id;
        return [
          detailId,
          {
            onOpenDetails: (): void => {
              handleDetailClick(detailId);
            },
            onToggleSelection: (checked: boolean): void => {
              handleDetailSelectionChange(detailId, checked);
            },
          },
        ];
      }),
    );
  }, [detailEntries, handleDetailClick, handleDetailSelectionChange]);

  return (
    <RowCard>
      <PersonSummaryDetails
        name={row.name}
        role={row.role}
        teamLabel={teamLabel ?? t("TimeReports.TeamFallbackLabel")}
        avatarUrl={avatarUrl}
        summary={summary}
        isExpanded={isExpanded}
        isZeroLikeValue={isZeroLikeValue}
        showDeviation={props.includeFlexTimeDeviations}
        labels={labels}
        onDownloadClick={handleDownloadClick}
        onToggleExpand={() => setIsExpanded((value) => !value)}
      />

      {isExpanded && (
        <DetailsList>
          <SelectAllRow>
            <Checkbox
              label={t("CompanyTimeReport.SelectAll")}
              checked={
                isAllSelected
                  ? "checked"
                  : isPartiallySelected
                    ? "indeterminate"
                    : "unchecked"
              }
              onChange={handleSelectAllChange}
            />
          </SelectAllRow>
          {detailEntries.map((entry) => (
            <PersonTimeReportRow
              key={entry.selection.detail.id}
              detailRow={entry.selection.detail}
              updatedAtLabel={entry.updatedAtLabel}
              isSelected={selectedDetailIdSet.has(entry.selection.detail.id)}
              getTagIconColor={getTagIconColor}
              onOpenDetails={
                rowInteractionHandlersById.get(entry.selection.detail.id)!
                  .onOpenDetails
              }
              onToggleSelection={
                rowInteractionHandlersById.get(entry.selection.detail.id)!
                  .onToggleSelection
              }
            />
          ))}
        </DetailsList>
      )}
    </RowCard>
  );
};
